import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';

// Route to breadcrumb mapping
const routeLabels: Record<string, { label: string; parent?: string }> = {
  '/museum': { label: 'Museum' },
  '/exhibits': { label: 'Exhibits' },
  '/collections': { label: 'Collections' },
  '/collections/firearms': { label: 'Firearms', parent: '/collections' },
  '/texas-rangers': { label: 'Texas Rangers' },
  '/texas-ranger-hall': { label: 'Ranger Hall of Fame', parent: '/texas-rangers' },
  '/visit': { label: 'Visit' },
  '/contact': { label: 'Contact' },
  // Heritage - Schreiner Legacy
  '/schreiner-dynasty': { label: 'Schreiner Dynasty' },
  '/captain-schreiner': { label: 'Captain Schreiner', parent: '/schreiner-dynasty' },
  '/schreiner-iii': { label: 'Charles III', parent: '/schreiner-dynasty' },
  '/gun-room': { label: 'The Gun Room', parent: '/schreiner-iii' },
  '/myrtle-schreiner': { label: 'Myrtle Schreiner', parent: '/schreiner-dynasty' },
  '/walter-schreiner': { label: 'Walter Schreiner', parent: '/schreiner-dynasty' },
  // Heritage - Ranch History
  '/ranch-history': { label: 'Ranch History' },
  '/wildlife-heritage': { label: 'Wildlife Heritage', parent: '/ranch-history' },
  '/exotic-species': { label: 'Exotic Species', parent: '/ranch-history' },
  '/yo-sam-houston': { label: 'Y.O. Sam Houston', parent: '/ranch-history' },
  // Heritage - Culture & Fame
  '/famous-visitors': { label: 'Famous Visitors' },
  '/hollywood-media': { label: 'Hollywood & Media', parent: '/famous-visitors' },
  '/literary-legacy': { label: 'Literary Legacy', parent: '/famous-visitors' },
  // Other
  '/heritage': { label: 'Heritage' },
  '/stewardship': { label: 'Stewardship' },
};

interface BreadcrumbProps {
  className?: string;
}

const Breadcrumb = ({ className }: BreadcrumbProps) => {
  const location = useLocation();
  const pathname = location.pathname;

  // Don't show breadcrumbs on home page
  if (pathname === '/') return null;

  // Build breadcrumb trail
  const buildTrail = (): Array<{ label: string; href: string }> => {
    const trail: Array<{ label: string; href: string }> = [];
    
    // Check for dynamic routes (exhibits/:slug, collections/:type, artifacts/:slug)
    const pathParts = pathname.split('/').filter(Boolean);
    
    if (pathParts[0] === 'exhibits' && pathParts[1] && !routeLabels[pathname]) {
      trail.push({ label: 'Exhibits', href: '/exhibits' });
      // Format slug to title case
      const exhibitName = pathParts[1].split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      trail.push({ label: exhibitName, href: pathname });
      return trail;
    }
    
    if (pathParts[0] === 'collections' && pathParts[1] && !routeLabels[pathname]) {
      trail.push({ label: 'Collections', href: '/collections' });
      const collectionName = pathParts[1].split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      trail.push({ label: collectionName, href: pathname });
      return trail;
    }

    if (pathParts[0] === 'artifacts' && pathParts[1]) {
      trail.push({ label: 'Collections', href: '/collections' });
      const artifactName = pathParts[1].split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      trail.push({ label: artifactName, href: pathname });
      return trail;
    }

    // Standard route lookup
    const routeInfo = routeLabels[pathname];
    if (!routeInfo) return trail;

    // If has parent, add parent first
    if (routeInfo.parent) {
      const parentInfo = routeLabels[routeInfo.parent];
      if (parentInfo) {
        trail.push({ label: parentInfo.label, href: routeInfo.parent });
      }
    }

    trail.push({ label: routeInfo.label, href: pathname });
    return trail;
  };

  const trail = buildTrail();
  if (trail.length === 0) return null;

  return (
    <nav 
      aria-label="Breadcrumb" 
      className={cn(
        "container mx-auto px-4 sm:px-6 lg:px-8 py-4 text-neutral-700",
        className
      )}
    >
      <ol className="flex items-center gap-2 text-sm">
        <li>
          <Link 
            to="/" 
            className="text-neutral-600 hover:text-neutral-900 transition-colors flex items-center gap-1"
            aria-label="Home"
          >
            <Home className="w-4 h-4" />
          </Link>
        </li>
        
        {trail.map((item, index) => {
          const isLast = index === trail.length - 1;
          
          return (
            <li key={item.href} className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-neutral-400" />
              {isLast ? (
                <span className="font-sans text-xs tracking-wide uppercase text-neutral-900">
                  {item.label}
                </span>
              ) : (
                <Link 
                  to={item.href}
                  className="font-sans text-xs tracking-wide uppercase text-neutral-600 hover:text-neutral-900 transition-colors"
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
